import * as React from 'react';

import { PurchaseLoadStatus } from '@rematter/frontend-lib-api-client';
import {
  DataGridColumn,
  formatMoney,
  LocalizedDate,
  maybeEmptyInfo,
  MeasuredValue,
} from '@rematter/ui-web';
import { useAtomValue } from 'jotai';

import {
  $columnReceivedDateRange,
  $columnSelectedFacilityIds,
  $columnSelectedInboundStatuses,
  $columnSelectedSupplierIds,
} from '../../../_state/transactions.state';
import { useTranslations } from '../../../i18n/useTranslations';
import { InboundTransactionRecord } from '../../../types/transaction';
import { PaymentStatusBadge } from '../../_shared/PaymentStatusBadge';
import { PurchaseLoadStatusBadge } from '../../_shared/StatusBadges';
import { DateRangeColumnFilter } from '../filters/DateRangeColumnFilter';
import { FacilityColumnFilter } from '../filters/FacilityColumnFilter';
import { InboundStatusColumnFilter } from '../filters/InboundStatusColumnFilter';
import { InboundSupplierColumnFilter } from '../filters/InboundSupplierColumnFilter';
import { InboundTransactionActionMenu } from '../InboundTransactionActionMenu';

interface TransactionsInboundColumnsProps {
  onDoubleClick?: (row: InboundTransactionRecord) => void;
}

interface DoubleClickProps {
  row: InboundTransactionRecord;
}

export const useTransactionsInboundColumns = ({
  onDoubleClick,
}: TransactionsInboundColumnsProps) => {
  const t = useTranslations();

  // Atoms
  const columnSelectedStatuses = useAtomValue($columnSelectedInboundStatuses);
  const columnSelectedSupplierIds = useAtomValue($columnSelectedSupplierIds);
  const columnSelectedFacilityIds = useAtomValue($columnSelectedFacilityIds);
  const columnReceivedDateRange = useAtomValue($columnReceivedDateRange);

  // Memoized values
  const columns = React.useMemo(() => {
    const handleCellDoubleClick = ({ row }: DoubleClickProps) => {
      onDoubleClick?.(row);
    };

    const columnDefs: DataGridColumn<InboundTransactionRecord>[] = [
      {
        key: 'receivedDate',
        header: t.transactionsView.table.columns.receivedDate,
        sortable: true,
        cell: ({ row }) =>
          maybeEmptyInfo(row.receivedDate, (v) => <LocalizedDate date={v} />),
        columnFilter: ({ close }) => (
          <DateRangeColumnFilter
            stateAtom={$columnReceivedDateRange}
            label={t.transactionsView.filters.receivedDate.label}
            ariaLabel={t.transactionsView.filters.receivedDate.ariaLabel}
            onClose={close}
          />
        ),
        isFilterActive: columnReceivedDateRange !== null,
      },
      {
        key: 'documentNumber',
        header: t.transactionsView.table.columns.documentNumber,
        sortable: true,
        cell: ({ row }) =>
          maybeEmptyInfo(row.documentNumber, (v) => String(v).padStart(6, '0')),
      },
      {
        key: 'purchaseOrderNumber',
        header: t.transactionsView.table.columns.purchaseOrderNumber,
        cell: ({ row }) => maybeEmptyInfo(row.purchaseOrderNumber),
      },
      {
        key: 'billNumber',
        header: t.transactionsView.table.columns.billNumber,
        cell: ({ row }) =>
          maybeEmptyInfo(row.billNumber, (v) =>
            row.billId ? (
              <a
                href={`/finance/bills/${row.billId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
                onClick={(e) => e.stopPropagation()}
              >
                {v}
              </a>
            ) : (
              v
            ),
          ),
      },
      {
        key: 'facility',
        header: t.transactionsView.table.columns.facility,
        cell: ({ row }) => maybeEmptyInfo(row.facility?.facilityName),
        columnFilter: ({ close }) => (
          <FacilityColumnFilter
            stateAtom={$columnSelectedFacilityIds}
            onClose={close}
          />
        ),
        isFilterActive: columnSelectedFacilityIds.size > 0,
      },
      {
        key: 'supplierType',
        header: t.transactionsView.table.columns.supplierType,
        cell: ({ row }) => maybeEmptyInfo(row.supplierAccount?.type),
      },
      {
        key: 'supplier',
        header: t.transactionsView.table.columns.supplier,
        cell: ({ row }) => maybeEmptyInfo(row.supplierAccount?.name),
        columnFilter: ({ close }) => (
          <InboundSupplierColumnFilter onClose={close} />
        ),
        isFilterActive: columnSelectedSupplierIds.size > 0,
      },
      {
        key: 'status',
        header: t.transactionsView.table.columns.status,
        cell: ({ row }) =>
          maybeEmptyInfo(row.status, (v) => (
            <PurchaseLoadStatusBadge status={v as PurchaseLoadStatus} />
          )),
        columnFilter: ({ close }) => (
          <InboundStatusColumnFilter onClose={close} />
        ),
        isFilterActive: columnSelectedStatuses.size > 0,
      },
      {
        key: 'paymentStatus',
        header: t.transactionsView.table.columns.paymentStatus,
        cell: ({ row }) =>
          maybeEmptyInfo(row.paymentStatus, (v) => (
            <PaymentStatusBadge status={v} />
          )),
      },
      {
        key: 'outstandingBalance',
        header: t.transactionsView.table.columns.outstandingBalance,
        cell: ({ row }) =>
          maybeEmptyInfo(row.outstandingBalance, (v) =>
            formatMoney(v.amount, v.currencyCode),
          ),
      },
      {
        key: 'netWeight',
        header: t.transactionsView.table.columns.netWeight,
        cell: ({ row }) =>
          maybeEmptyInfo(row.totalNetWeight, (w) => (
            <MeasuredValue value={w.value} unit={w.unit} />
          )),
      },
      {
        key: 'grandTotal',
        header: t.transactionsView.table.columns.grandTotal,
        cell: ({ row }) =>
          maybeEmptyInfo(row.grandTotal, (v) =>
            formatMoney(v.amount, v.currencyCode),
          ),
      },
      {
        key: 'externalNotes',
        header: t.transactionsView.table.columns.externalNotes,
        cell: ({ row }) => maybeEmptyInfo(row.externalNotes),
      },
      {
        key: 'actions',
        header: '',
        width: 60,
        pinned: 'right' as const,
        cell: ({ row }) => <InboundTransactionActionMenu transaction={row} />,
      },
    ];

    return columnDefs.map((it) => ({
      ...it,
      onDoubleClick: it.key === 'actions' ? undefined : handleCellDoubleClick,
    }));
  }, [
    t,
    columnSelectedStatuses,
    columnSelectedSupplierIds,
    columnSelectedFacilityIds,
    columnReceivedDateRange,
    onDoubleClick,
  ]);

  const columnVisibilityState = React.useMemo(
    () => Object.fromEntries(columns.map((column) => [column.key, true])),
    [columns],
  );

  return { columns, columnVisibilityState };
};
